import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Business,
  Save,
  Add,
  Edit,
  Delete,
  Refresh,
  Person,
  Check,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectCurrentCompany, setAuthData } from '@/store/slices/authSlice';
import { STORAGE_KEYS } from '@/utils/constants';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface Company {
  id: string;
  name: string;
  legalName: string;
  document: string;
  documentType: string;
  email: string;
  phone: string;
  status: string;
  plan: string;
  sicoobNumeroCliente: string;
  userRole: string;
  isDefault: boolean;
  isOwner: boolean;
}

const CompanyPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentCompany = useAppSelector(selectCurrentCompany);

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    legalName: '',
    document: '',
    documentType: 'PJ',
    email: '',
    phone: '',
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await api.get('/user/companies');
      if (response?.success) {
        setCompanies(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading companies:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        legalName: company.legalName,
        document: company.document,
        documentType: company.documentType,
        email: company.email,
        phone: company.phone || '',
      });
    } else {
      setEditingCompany(null);
      setFormData({
        name: '',
        legalName: '',
        document: '',
        documentType: 'PJ',
        email: '',
        phone: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompany(null);
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const formatDocument = (doc: string, type: string) => {
    const cleanDoc = doc.replace(/\D/g, '');
    if (type === 'PF') {
      return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (editingCompany) {
        // Update existing company
        const response = await api.put(`/user/companies/${editingCompany.id}`, {
          name: formData.name,
          legalName: formData.legalName,
          email: formData.email,
          phone: formData.phone,
        });

        if (response?.success) {
          toast.success('Empresa atualizada com sucesso');
          handleCloseDialog();
          loadCompanies();
        } else {
          toast.error(response?.error?.message || 'Erro ao atualizar empresa');
        }
      } else {
        // Create new company
        const response = await api.post('/user/companies', {
          name: formData.name,
          legalName: formData.legalName,
          document: formData.document.replace(/\D/g, ''),
          documentType: formData.documentType,
          email: formData.email,
          phone: formData.phone,
        });

        if (response?.success) {
          toast.success('Empresa cadastrada com sucesso');
          handleCloseDialog();
          loadCompanies();
        } else {
          toast.error(response?.error?.message || 'Erro ao cadastrar empresa');
        }
      }
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast.error(error?.error?.message || 'Erro ao salvar empresa');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Minhas Empresas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie as empresas/contas vinculadas ao seu perfil
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadCompanies}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Nova Empresa
          </Button>
        </Box>
      </Box>

      {/* Cards de Resumo */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="primary">
                {companies.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Empresas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {companies.filter(c => c.isOwner).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Proprietário
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {companies.filter(c => !c.isOwner).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compartilhadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {companies.filter(c => c.documentType === 'PF').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pessoa Física
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Empresas */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>E-mail</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vínculo</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    Nenhuma empresa cadastrada
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={() => handleOpenDialog()}
                  >
                    Cadastrar Primeira Empresa
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow
                  key={company.id}
                  hover
                  sx={{
                    backgroundColor:
                      company.id === currentCompany?.id
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'transparent',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor:
                            company.documentType === 'PF'
                              ? alpha(theme.palette.info.main, 0.15)
                              : alpha(theme.palette.primary.main, 0.15),
                          color: company.documentType === 'PF' ? 'info.main' : 'primary.main',
                        }}
                      >
                        {company.documentType === 'PF' ? (
                          <Person />
                        ) : (
                          <Business />
                        )}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {company.name}
                          </Typography>
                          {company.id === currentCompany?.id && (
                            <Chip
                              icon={<Check sx={{ fontSize: 14 }} />}
                              label="Ativa"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {company.legalName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        label={company.documentType}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          mr: 1,
                          bgcolor:
                            company.documentType === 'PF'
                              ? alpha(theme.palette.info.main, 0.15)
                              : alpha(theme.palette.warning.main, 0.15),
                          color: company.documentType === 'PF' ? 'info.dark' : 'warning.dark',
                        }}
                      />
                      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                        {formatDocument(company.document, company.documentType)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{company.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.status === 'active' ? 'Ativa' : company.status}
                      size="small"
                      color={company.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {company.isOwner ? (
                      <Chip
                        label="Proprietário"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.warning.main, 0.15),
                          color: 'warning.dark',
                        }}
                      />
                    ) : (
                      <Chip
                        label="Compartilhada"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.15),
                          color: 'info.dark',
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(company)}
                        disabled={!company.isOwner}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configurar Sicoob">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={async () => {
                          if (company.id === currentCompany?.id) {
                            navigate('/settings/sicoob');
                            return;
                          }

                          try {
                            const response = await api.put('/user/selected-company', { companyId: company.id });
                            if (response?.success) {
                              const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
                              const user = userData ? JSON.parse(userData) : null;
                              const newCompanyData = {
                                id: company.id,
                                name: company.name,
                                document: company.document,
                                tradingName: company.name,
                                legalName: company.legalName,
                              };
                              localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(newCompanyData));
                              dispatch(setAuthData({
                                user,
                                company: newCompanyData,
                                token: localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
                                refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
                              }));
                              toast.success(`Empresa alterada para ${company.name}`);
                              navigate('/settings/sicoob');
                            }
                          } catch (error: any) {
                            toast.error(error?.error?.message || 'Erro ao alterar empresa');
                          }
                        }}
                      >
                        <Settings fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para Nova/Editar Empresa */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {!editingCompany && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Pessoa</InputLabel>
                    <Select
                      value={formData.documentType}
                      label="Tipo de Pessoa"
                      onChange={handleChange('documentType') as any}
                    >
                      <MenuItem value="PJ">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business fontSize="small" />
                          Pessoa Jurídica (CNPJ)
                        </Box>
                      </MenuItem>
                      <MenuItem value="PF">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" />
                          Pessoa Física (CPF)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={formData.documentType === 'PF' ? 'Nome Completo' : 'Nome Fantasia'}
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={formData.documentType === 'PF' ? 'Nome Completo (Documento)' : 'Razão Social'}
                  value={formData.legalName}
                  onChange={handleChange('legalName')}
                  helperText={formData.documentType === 'PF' ? 'Conforme consta no CPF' : ''}
                />
              </Grid>
              {!editingCompany && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={formData.documentType === 'PF' ? 'CPF' : 'CNPJ'}
                    value={formData.document}
                    onChange={handleChange('document')}
                    required
                    placeholder={formData.documentType === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                    helperText="Documento não poderá ser alterado após o cadastro"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="E-mail"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Telefone"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  placeholder="(00) 00000-0000"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !formData.name || !formData.email || (!editingCompany && !formData.document)}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyPage;
